import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ApiErrorCode, PRINCIPAL_TYPE_LABELS, PrincipalType } from '@fleet/shared';
import { directoryApi, type DirectoryPrincipal } from '../../api/permissions';
import { ApiError } from '../../api/client';
import { inputClass } from '../../ui/Field';

/** Mindestlänge der Suche — dieselbe Grenze prüft das Schema serverseitig. */
const MIN_LENGTH = 2;

function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
}

/**
 * Typeahead gegen das Entra-Verzeichnis.
 *
 * Die Entprellung ist hier nicht bloß Höflichkeit: Der Endpunkt ist auf
 * 30 Anfragen pro Minute gedrosselt, und vor jedem Aufruf läuft zusätzlich die
 * Rollenprüfung. Ohne Verzögerung wären zwei Sekunden Tippen das Kontingent.
 */
export function PrincipalPicker({
  onSelect,
  disabled,
  only,
}: {
  onSelect: (principal: DirectoryPrincipal) => void;
  disabled?: boolean;
  /**
   * Auf eine Art festlegen und die Auswahl ausblenden.
   *
   * Gebraucht beim Anlegen eines Nutzers: Eine Entra-Gruppe lässt sich nicht
   * als Person eintragen. Eine Auswahl anzubieten, die nur eine gültige Antwort
   * hat, wäre eine Falle — und die Meldung käme erst vom Server.
   */
  only?: PrincipalType;
}) {
  const [query, setQuery] = useState('');
  const [chosenType, setChosenType] = useState<PrincipalType | ''>('');
  const type = only ?? chosenType;
  const debounced = useDebounced(query.trim(), 300);

  const search = useQuery({
    queryKey: ['directory', debounced, type],
    queryFn: () => directoryApi.search(debounced, type || undefined),
    enabled: debounced.length >= MIN_LENGTH,
    staleTime: 60_000,
    // Bei einer Drosselung nicht nachlegen — das verschärft nur die Lage.
    retry: false,
    placeholderData: keepPreviousData,
  });

  const error = search.error;
  const isUnavailable =
    error instanceof ApiError && error.code === ApiErrorCode.DIRECTORY_UNAVAILABLE;
  const isThrottled = error instanceof ApiError && error.status === 429;

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={only === PrincipalType.USER ? 'Person suchen …' : 'Person oder Gruppe suchen …'}
          className={inputClass}
          disabled={disabled}
          aria-label="Verzeichnis durchsuchen"
        />
        {!only && (
          <select
            value={chosenType}
            onChange={(event) => setChosenType(event.target.value as PrincipalType | '')}
            className={`${inputClass} w-40`}
            disabled={disabled}
            aria-label="Auf Art einschränken"
          >
            <option value="">Alle</option>
            {Object.values(PrincipalType).map((value) => (
              <option key={value} value={value}>
                {PRINCIPAL_TYPE_LABELS[value]}
              </option>
            ))}
          </select>
        )}
      </div>

      {query.trim().length > 0 && query.trim().length < MIN_LENGTH && (
        <p className="text-xs text-slate-500">Mindestens {MIN_LENGTH} Zeichen eingeben.</p>
      )}

      {isUnavailable && (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          Das Verzeichnis ist nicht erreichbar. Das ist <strong>nicht</strong> dasselbe wie
          „keine Treffer" — vorhandene Berechtigungen bleiben davon unberührt.
        </p>
      )}

      {isThrottled && (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          Zu viele Suchanfragen in kurzer Zeit. Einen Moment warten, dann geht es weiter.
        </p>
      )}

      {search.isFetching && <p className="text-xs text-slate-500">Wird gesucht …</p>}

      {search.data && search.data.length === 0 && !search.isFetching && (
        <p className="text-xs text-slate-500">Keine Treffer.</p>
      )}

      {search.data && search.data.length > 0 && (
        <ul className="max-h-60 divide-y divide-slate-100 overflow-y-auto rounded-md border border-slate-200">
          {search.data.map((principal) => (
            <li key={`${principal.principalType}:${principal.entraObjectId}`}>
              <button
                type="button"
                onClick={() => {
                  onSelect(principal);
                  setQuery('');
                }}
                disabled={disabled}
                className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-slate-50"
              >
                <span>
                  <span className="font-medium">{principal.displayName}</span>
                  {principal.secondary && (
                    <span className="block text-xs text-slate-500">{principal.secondary}</span>
                  )}
                </span>
                <span className="shrink-0 text-xs text-slate-400">
                  {PRINCIPAL_TYPE_LABELS[principal.principalType]}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
